import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import React, { useEffect } from 'react';
import { SortableAccountDistributionItem } from './SortableAccountDistributionItem';
import { AccountDistributionOrderOptionDTO } from './api/useAccountDistributionOptionsQuery';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { AddAccountDistributionItemModal } from './AddAccountDistributionItemModal';

interface AccountDistributionOrderListProps {
  options: AccountDistributionOrderOptionDTO[];
  order: AccountDistributionOrderOptionDTO[];
  defaultOrder: AccountDistributionOrderOptionDTO[];
  onOrderChange: (newOptions: AccountDistributionOrderOptionDTO[]) => void;
}

export const AccountDistributionOrderList = ({
  options,
  order,
  defaultOrder,
  onOrderChange,
}: AccountDistributionOrderListProps) => {
  const [isAddDistributionItemModalOpen, setIsAddDistributionItemModalOpen] = React.useState(false);

  const availableOptions = options.filter(
    option => !order.some(orderedOption => orderedOption.id === option.id),
  );

  useEffect(() => {
    if (availableOptions.length === 0) {
      setIsAddDistributionItemModalOpen(false);
    }
  }, [availableOptions.length]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = order.findIndex(item => item.id === active.id);
      const newIndex = order.findIndex(item => item.id === over.id);

      onOrderChange(arrayMove(order, oldIndex, newIndex));
    }
  }

  function handleDelete(id: string) {
    onOrderChange(order.filter(item => item.id !== id));
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      <Box display="flex" justifyContent="flex-end">
        <Button variant="link" leftIcon="far fa-undo" onClick={() => onOrderChange(defaultOrder)}>
          Reset to Default
        </Button>
      </Box>

      <AddAccountDistributionItemModal
        isOpen={isAddDistributionItemModalOpen}
        onClose={() => setIsAddDistributionItemModalOpen(false)}
        options={availableOptions}
        onAdd={item => {
          onOrderChange([...order, item]);
        }}
      />

      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext items={order}>
          <Box display="flex" flexDirection="column" gridGap={4}>
            {order.map(option => {
              return <SortableAccountDistributionItem {...option} onDelete={handleDelete} />;
            })}
          </Box>
        </SortableContext>
      </DndContext>

      <Button
        variant="secondary"
        leftIcon="far fa-plus"
        onClick={() => setIsAddDistributionItemModalOpen(true)}
        disabled={availableOptions.length === 0}
      >
        Add Excluded Accounts to the List
      </Button>
    </Box>
  );
};
